import { isRoleLessThan, KastWebSdkClient } from "@kalyzee/kast-web-sdk";
import { UserRole } from "@kalyzee/kast-web-sdk/http";
import { AverPtzClient, PtzClient } from "@kalyzee/kast-web-sdk/web-components";
import { RoomOnCustomEventActionHandler } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useRef } from "react";
import { MeetingSourceExtended } from "./meeting";
import { useSettings } from "./settings";
import { useSdkContext } from "../contexts/sdk/context";

const ACTIONS = ["move", "stopMove", "zoom", "stopZoom", "setPreset", "loadPreset"] as const;
const MOVE_DIRECTIONS = ["left", "right", "up", "down"] as const;
const ZOOM_DIRECTIONS = ["in", "out"] as const;

interface PtzActionMessage<T extends (typeof ACTIONS)[number]> {
  id: string;
  action: T;
  content: {
    move: { direction: (typeof MOVE_DIRECTIONS)[number] };
    stopMove: { direction: (typeof MOVE_DIRECTIONS)[number] | undefined };
    zoom: { direction: (typeof ZOOM_DIRECTIONS)[number] };
    stopZoom: { direction: (typeof ZOOM_DIRECTIONS)[number] | undefined };
    setPreset: { index: number };
    loadPreset: { index: number };
  }[T];
}

export const useDeviceControls = () => {
  const sdkCtx = useSdkContext(); 
  const [settings] = useSettings();
  const ptzClientsRef = useRef<PtzClient<string>[]>([]);

  const processAction = useCallback(async (id: string, action: (typeof ACTIONS)[number], content: any): Promise<boolean> => {
    const client = ptzClientsRef.current.find((c) => c.data === id);
    // console.log("Client: ", client, ptzClientsRef);
    if (!client) return false;

    // MOVE
    if (action === "move") {
      if (!content.direction || !MOVE_DIRECTIONS.includes(content.direction)) return false;
      const direction: (typeof MOVE_DIRECTIONS)[number] = content.direction;
      console.log("Move: ", direction, client);
      return await client.move(direction);
    }
    if (action === "stopMove") {
      if (content.direction && !MOVE_DIRECTIONS.includes(content.direction)) return false;
      const direction: (typeof MOVE_DIRECTIONS)[number] | undefined = content.direction;
      return await client.stopMove(direction);
    }

    // ZOOM
    if (action === "zoom") {
      if (!content.direction || !ZOOM_DIRECTIONS.includes(content.direction)) return false;
      const direction: (typeof ZOOM_DIRECTIONS)[number] = content.direction;
      return await client.zoom(direction);
    }
    if (action === "stopZoom") {
      if (content.direction && !ZOOM_DIRECTIONS.includes(content.direction)) return false;
      const direction: (typeof ZOOM_DIRECTIONS)[number] | undefined = content.direction;
      return await client.stopZoom(direction);
    }

    // PRESET
    if (action === "setPreset") {
      const index = Number(content.index);
      if (index === undefined || isNaN(index)) return false;
      return await client.setPreset(index);
    }
    if (action === "loadPreset") {
      const index = Number(content.index);
      if (index === undefined || isNaN(index)) return false;
      return await client.loadPreset(index);
    }
    return false;
  }, []);

  // Get message
  useEffect(() => {
    const websocket = sdkCtx.client.getWebsocketClient();
    const handler = new RoomOnCustomEventActionHandler(); //
    handler.onReceive = (message) => {
      console.log("Custom event: ", message);
      const data = message.content.data;
      const user = message.content.fromUser;

      if (isRoleLessThan(user.role, UserRole.TECHNICIAN)) return; // unauthorized

      if (typeof data === "object") {
        if (
          "action" in data &&
          "content" in data &&
          "id" in data &&
          typeof data.action === "string" &&
          typeof data.content === "object" &&
          typeof data.id === "string"
        ) {
          if (ACTIONS.includes(data.action)) {
            console.log("Process action : ", data);
            processAction(data.id, data.action as any, data.content);
          }
        }
      }
    };
    websocket.registerActionHandler(handler);
    return () => {
      websocket.unregisterActionHandler(handler);
    };
  }, [sdkCtx.client, processAction]);

  // Update clients
  useEffect(() => {
    const clientsToRemove: PtzClient<string>[] = [];

    const deviceControlEnabled = (source: Partial<MeetingSourceExtended>) => {
      return source.id && source?.controls?.type === "aver-ptz";
    };

    ptzClientsRef.current.forEach((c) => {
      const id = c.data;
      if (!settings?.sources?.find((s) => deviceControlEnabled(s) && s.id === id)) {
        clientsToRemove.push(c);
      }
    });
    ptzClientsRef.current = ptzClientsRef.current.filter((d) => !clientsToRemove.find((d2) => d === d2));
    settings?.sources?.forEach((s) => {
      const id = s.id;
      const controls = s.controls;
      if (deviceControlEnabled(s) && controls && !ptzClientsRef.current.find((c) => c.data === id)) {
        if (controls.type === "aver-ptz" && controls.hostname) {
          const client = new AverPtzClient({ hostname: controls.hostname, username: controls.username, password: controls.password });
          client.data = id;
          ptzClientsRef.current.push(client);
        }
      }
    });
  }, [settings]);
};

export const pushDeviceControlsCmd = <T extends (typeof ACTIONS)[number]>(
  client: KastWebSdkClient,
  roomId: string,
  deviceId: string,
  action: T,
  content: PtzActionMessage<T>["content"]
) => {
  const websocket = client.getWebsocketClient();
  const message: PtzActionMessage<T> = {
    id: deviceId,
    action,
    content,
  };
  // console.log("Push action : ", message);
  websocket.emitter.room.publishCustomEvent({ id: roomId, data: message });
};
